import { Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs';
import { ShowUserFacade } from '../../../../../Facades/Intune/show-user.facade';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { ResetPasswordDialogComponent } from '../../dialogs/reset-password-dialog/reset-password-dialog.component';
import { PasswordSuccessDialogComponent } from '../../dialogs/password-success-dialog/password-success-dialog.component';
import { ResetPasswordPayload } from '../../../../../Interfaces/api/Intune/users.interface';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { AuthService } from '../../../../Shared/Services/auth.service';

@Component({
  selector: 'app-user-actions',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslateModule],
  templateUrl: './user-actions.component.html',
  styleUrls: ['./user-actions.component.scss'],
})
export class UserActionsComponent {
  private readonly _showUserFacade = inject(ShowUserFacade);
  private readonly _dialog = inject(MatDialog);
  private readonly _msg = inject(MsgService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _authService = inject(AuthService);

  readonly permissions = PERMISSIONS;
  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  private readonly _actionDefs: { permission: PERMISSIONS; label: string; icon: string; action: () => void; class?: string }[] = [
    { permission: PERMISSIONS.INTUNE_USER_RESET_PASSWORD, label: 'app.user.actions.reset_password.label', icon: 'lock_reset', action: () => this.resetPassword(), class: 'icon-reset-password' },
  ];

  readonly visibleActions = computed(() => {
    const accessibility = this._showUserFacade.user()?.actions_accessibility;
    return this._actionDefs.filter(a =>
      this.currentUser()?.hasPermission(a.permission) &&
      accessibility?.find(x => x.permission_name === a.permission)?.accessible !== false
    );
  });

  resetPassword(): void {
    this._openResetPasswordDialog()
      .afterClosed()
      .pipe(
        filter((payload): payload is ResetPasswordPayload => payload !== undefined),
        switchMap((payload) => this._executeResetPassword(payload)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (password) => this._onResetPasswordSuccess(password),
      });
  }

  private _openResetPasswordDialog() {
    return this._dialog.open<ResetPasswordDialogComponent, { title: string; message: string }, ResetPasswordPayload>(
      ResetPasswordDialogComponent,
      {
        data: {
          title: this._translate.instant(`${'app.user.actions.reset_password'}.title`),
          message: this._translate.instant(`${'app.user.actions.reset_password'}.message`),
        },
        panelClass: 'av-dialog',
      }
    );
  }

  private _executeResetPassword(payload: ResetPasswordPayload) {
    return this._showUserFacade.resetPassword(payload).pipe(
      map(() => payload.password)
    );
  }

  private _onResetPasswordSuccess(password: string): void {
    this._msg.showSnackBar('app.api.success.users.reset_password');
    this._openPasswordSuccessDialog(password);
  }

  private _openPasswordSuccessDialog(password: string): void {
    this._dialog.open<PasswordSuccessDialogComponent, { title: string; password: string }>(
      PasswordSuccessDialogComponent,
      {
        data: {
          title: this._translate.instant(`${'app.user.actions.reset_password'}.success.title`),
          password,
        },
        panelClass: 'av-dialog',
      }
    );
  }
} 
